import { Children, type ReactElement, type ReactNode, useMemo } from 'react';
import { isElement } from 'react-is';

import { _isEmptyState } from '../components/DataTableEmptyState.js';
import {
  _isDataTableSelectedRowsActions,
  type DataTableSelectedRowsActionsProps,
} from '../components/TableActions/DataTableSelectedRowsActions.js';
import {
  _isTableActions,
  type DataTableTableActionsProps,
} from '../components/TableActions/DataTableTableActions.js';
import {
  _isDataTableToolbar,
  type DataTableToolbarProps,
} from '../components/Toolbar/DataTableToolbar.js';
import type { DataTablePaginationProps } from '../features/Pagination/pagination-types.js';
import { _isDataTablePagination } from '../features/Pagination/Pagination.js';
import type { DataTableRowData } from '../public.api.js';

/** @internal */
export type DataTableSlots<TData extends DataTableRowData> = {
  pagination: ReactElement<DataTablePaginationProps> | null;
  toolbarProps: DataTableToolbarProps | null;
  emptyState: ReactElement | null;
  tableActions: ReactElement<DataTableTableActionsProps> | null;
  selectedRowsActions: ReactElement<
    DataTableSelectedRowsActionsProps<TData>
  > | null;
};

/**
 * @internal
 */
export function useDataTableSlots<TData extends DataTableRowData>(
  children: ReactNode,
): DataTableSlots<TData> {
  return useMemo(() => {
    const slots: DataTableSlots<TData> = {
      pagination: null,
      toolbarProps: null,
      selectedRowsActions: null,
      emptyState: null,
      tableActions: null,
    };

    Children.forEach(children, (child) => {
      if (isElement(child)) {
        if (_isDataTableToolbar(child)) {
          slots.toolbarProps = child.props;
        }
        if (_isTableActions(child)) {
          slots.tableActions = child;
        }
        if (_isEmptyState(child)) {
          slots.emptyState = child;
        }
        if (_isDataTablePagination(child)) {
          slots.pagination = child;
        }
        if (_isDataTableSelectedRowsActions(child)) {
          slots.selectedRowsActions = child;
        }
      }
    });
    return slots;
  }, [children]);
}
